// Backend API Configuration
const isDevelopment = process.env.NODE_ENV === 'development';
// Strip trailing slash and a trailing /api so REACT_APP_API_URL may be either
// https://host or https://host/api without producing /api/api/...
const rawApiHost = (process.env.REACT_APP_API_URL ?? '')
  .trim()
  .replace(/\/$/, '')
  .replace(/\/api$/i, '');

function withApiSuffix(host) {
  if (!host) return '/api';
  return `${host}/api`;
}

// Dev: default CRA proxy → /api. If REACT_APP_API_URL is set, setupProxy is disabled (see setupProxy.js)
// so we must call the backend host directly. Prod: explicit host or same-origin /api.
const API_BASE_URL =
  isDevelopment && rawApiHost !== ''
    ? withApiSuffix(rawApiHost)
    : isDevelopment
      ? '/api'
      : rawApiHost !== ''
        ? withApiSuffix(rawApiHost)
        : '/api';

// For helpers / legacy. Dev without REACT_APP_API_URL uses CRA proxy → relative /api only;
// do not default to a remote VM (breaks resume formatter and other local services).
const BASE_URL = rawApiHost;

export const ENDPOINTS = {
  prefix: BASE_URL,

  // API Base URLs
  API_BASE: API_BASE_URL,

  // Authentication endpoints
  AUTH: {
    LOGIN: `${API_BASE_URL}/auth/login`,
    LOGOUT: `${API_BASE_URL}/auth/logout`,
    REFRESH: `${API_BASE_URL}/auth/refresh`,
    REQUEST_ACCESS: `${API_BASE_URL}/auth/request-access`,
  },

  // Job Description endpoints
  JD: {
    CREATE: `${API_BASE_URL}/job-descriptions`,
    GET_ALL: `${API_BASE_URL}/job-descriptions`,
    GET_BY_ID: (id) => `${API_BASE_URL}/job-descriptions/${id}`,
    UPDATE: (id) => `${API_BASE_URL}/job-descriptions/${id}`,
    DELETE: (id) => `${API_BASE_URL}/job-descriptions/${id}`,
    UPLOAD: `${API_BASE_URL}/job-descriptions/upload`,
  },

  // Resume endpoints
  RESUME: {
    UPLOAD: `${API_BASE_URL}/resumes/upload`,
    GET_ALL: `${API_BASE_URL}/resumes`,
    GET_BY_ID: (id) => `${API_BASE_URL}/resumes/${id}`,
    DELETE: (id) => `${API_BASE_URL}/resumes/${id}`,
    FORMAT: `${API_BASE_URL}/resumes/format`,
    // axios-instance-relative (see RESUME_TEMPLATES note below)
    FORMAT_TEMPLATES: `/resumes/format-templates`,
    FORMAT_COUNT: `/resumes/format-count`,
    FORMAT_FROM_DATA: (templateId) => `/resumes/format-from-data?templateId=${templateId}`,
    // Server-side document-native PDF (DOCX -> LibreOffice -> PDF). Feuji2/Feuji3 only.
    FORMAT_PDF_FROM_DATA: (templateId) => `/resumes/format-pdf-from-data?templateId=${templateId}`,
  },

  // Company resume templates (extract / save / list / standardize)
  // NOTE: intentionally RELATIVE paths (no API_BASE_URL prefix) — these are passed
  // straight into the shared axios instance whose baseURL already ends with '/api'.
  // Embedding the prefix here produces '/api/api/...' (Spring: no mapping).
  RESUME_TEMPLATES: {
    EXTRACT: `/resumes/templates/extract`,
    SAVE: `/resumes/templates/save`,
    LIST: `/resumes/templates/list`,
    GET_BY_ID: (id) => `/resumes/templates/${id}`,
    STANDARDIZE: (templateId) => `/resumes/templates/standardize?templateId=${templateId}`,
    // Deterministic dynamic engine v2 (no LLM extraction; DSL: hirecrux_company_template)
    EXTRACT_V2: `/resumes/templates/extract-v2`,
    STANDARDIZE_V2: (templateId) => `/resumes/templates/standardize-v2?templateId=${templateId}`,
    // Template Skeleton Extractor v3 (deterministic; header component +
    // pagination + kv grid confidence; DSL: hirecrux_company_template 3.0)
    EXTRACT_V3: `/resumes/templates/extract-v3`,
    STANDARDIZE_V3: (templateId) => `/resumes/templates/standardize-v3?templateId=${templateId}`,
  },

  // Analysis endpoints
  ANALYSIS: {
    STACK_RANKING: `${API_BASE_URL}/analysis/stack-ranking`,
    CANDIDATE_ANALYSIS: `${API_BASE_URL}/analysis/candidates`,
    SKILL_MATCHING: `${API_BASE_URL}/analysis/skill-matching`,
    SCORING_PARAMETERS: `${API_BASE_URL}/analysis/scoring-parameters`,
  },

  // Ceipal Integration endpoints
  CEIPAL: {
    JOBS: `${API_BASE_URL}/ceipal/jobs`,
    APPLICANTS: `${API_BASE_URL}/ceipal/applicants`,
    IMPORT: `${API_BASE_URL}/ceipal/import`,
    STATUS: `${API_BASE_URL}/ceipal/status`,
    CREDENTIALS: `${API_BASE_URL}/ceipal/credentials`,
    TOKENS: `${API_BASE_URL}/ceipal/tokens`,
    JOB_DESCRIPTION: (id) => `${API_BASE_URL}/ceipal/job-description/${id}`,
  },

  // WhatsApp Integration endpoints
  WHATSAPP: {
    SEND_MESSAGE: `${API_BASE_URL}/whatsapp/send-message`,
    WEBHOOK: `${API_BASE_URL}/webhook/whatsapp`,
    MESSAGES: `${API_BASE_URL}/whatsapp/messages`,
    STATUS: `${API_BASE_URL}/whatsapp/status`,
  },

  // File endpoints
  FILES: {
    UPLOAD: `${API_BASE_URL}/files/upload`,
    DOWNLOAD: (id) => `${API_BASE_URL}/files/download/${id}`,
    DELETE: (id) => `${API_BASE_URL}/files/${id}`,
  },

  // Health and monitoring endpoints
  HEALTH: {
    CHECK: `${API_BASE_URL}/actuator/health`,
    INFO: `${API_BASE_URL}/actuator/info`,
    METRICS: `${API_BASE_URL}/actuator/metrics`,
  },

  // Database endpoints
  DATABASE: {
    TABLES: `${API_BASE_URL}/database/tables`,
    QUERY: `${API_BASE_URL}/database/query`,
    BACKUP: `${API_BASE_URL}/database/backup`,
    RESTORE: `${API_BASE_URL}/database/restore`,
  },

  // Positions endpoints (for existing positions)
  POSITIONS: {
    GET_ALL: `${API_BASE_URL}/positions`,
    CREATE: `${API_BASE_URL}/positions`,
    GET_BY_ID: (id) => `${API_BASE_URL}/positions/${id}`,
    UPDATE: (id) => `${API_BASE_URL}/positions/${id}`,
    DELETE: (id) => `${API_BASE_URL}/positions/${id}`,
    SCREENING_QUESTIONS: (id) => `${API_BASE_URL}/positions/${id}/screening-questions`,
    GENERATE_SCREENING_QUESTIONS: (id) => `${API_BASE_URL}/positions/${id}/screening-questions/generate`,
  },

  // Tenant management (Super Admin)
  TENANTS: {
    GET_ALL: `${API_BASE_URL}/tenants`,
    CREATE: `${API_BASE_URL}/tenants`,
    UPDATE: (id) => `${API_BASE_URL}/tenants/${id}`,
    DELETE: (id) => `${API_BASE_URL}/tenants/${id}`,
    USERS: (tenantId) => `${API_BASE_URL}/tenants/${tenantId}/users`,
    USER: (tenantId, userId) => `${API_BASE_URL}/tenants/${tenantId}/users/${userId}`,
  },

  // Admin panel (Super Admin + Tenant Admin)
  ADMIN: {
    MY_TENANT: `${API_BASE_URL}/admin/my-tenant`,
  },
};

// Helper function to get full URL
export const getFullUrl = (endpoint) => {
  if (endpoint.startsWith('http')) return endpoint;
  if (BASE_URL) return `${BASE_URL}${endpoint}`;
  return endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
};

// Helper function to build query parameters
export const buildQueryString = (params) => {
  const queryString = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined) {
      queryString.append(key, params[key]);
    }
  });
  return queryString.toString();
};

// Helper function to get endpoint with query parameters
export const getEndpointWithQuery = (baseEndpoint, queryParams = {}) => {
  const queryString = buildQueryString(queryParams);
  return queryString ? `${baseEndpoint}?${queryString}` : baseEndpoint;
};

export default ENDPOINTS;
